import { CheckIcon } from "$components/icons";
import { styled, type Theme } from "@material-ui/core";
import type { ComponentProps, ReactNode } from "react";
import {
  InvoiceCardSection,
  useInvoiceCardLayout,
} from "../invoice-card-shell";
import { INVOICE_CARD_COLORS } from "./tokens";

const FooterRow = styled("div")<
  Theme,
  { "data-pad-x": number; "data-pad-y": number }
>(({ theme, ...props }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.25),
  padding: `${props["data-pad-y"]}px ${props["data-pad-x"]}px`,
}));

const ActionCluster = styled("div")<Theme, { "data-icon-padding": number }>(
  ({ ...props }) => ({
    display: "flex",
    alignItems: "center",
    gap: 4,
    // Reached through the class rather than by styling IconButton directly:
    // it is a function component, and MUI v4's styled() can emit a rule-less
    // class when it wraps one.
    "& .MuiIconButton-root": { padding: props["data-icon-padding"] },
  }),
);

const SettledNote = styled("div")({
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  fontWeight: 600,
  fontSize: 13.5,
  color: INVOICE_CARD_COLORS.paidText,
  "& .MuiSvgIcon-root": { fontSize: 16 },
});

const PrimaryActionSlot = styled("div")<
  Theme,
  { "data-full-width": boolean; "data-min-height": number }
>(({ ...props }) => ({
  marginLeft: "auto",
  display: "flex",
  justifyContent: "flex-end",
  ...(props["data-full-width"] && { flex: "1 1 auto" }),
  "& .MuiButton-root": {
    minHeight: props["data-min-height"],
    ...(props["data-full-width"] && { width: "100%" }),
  },
}));

/**
 * Which affordances a user gets is a permissions question answered upstream, so
 * every control arrives as a node.
 */
export const InvoiceFooterBar: FC<
  {
    mode: "draft" | "finalised";
    actions?: ReactNode;
    primaryAction?: ReactNode;
    /** Replaces the primary action once a finalised invoice is settled. */
    settledNote?: ReactNode;
  } & Omit<ComponentProps<typeof InvoiceCardSection>, "children">
> = ({ mode, actions, primaryAction, settledNote, ...restProps }) => {
  const { isCompact, iconButtonPadding, minTouchTarget } =
    useInvoiceCardLayout();

  return (
    <InvoiceCardSection
      tone="alt"
      // The footer sits tighter than the card's padX so the icon buttons' own
      // padding lines their glyphs up with the sections above.
      disablePadX
      paddingTop={0}
      paddingBottom={0}
      {...restProps}
    >
      <FooterRow
        data-pad-x={mode === "draft" || !isCompact ? 16 : 12}
        data-pad-y={mode === "draft" ? 12 : 10}
      >
        {actions && (
          <ActionCluster data-icon-padding={iconButtonPadding}>
            {actions}
          </ActionCluster>
        )}
        {settledNote && (
          <SettledNote>
            <CheckIcon color="inherit" />
            {settledNote}
          </SettledNote>
        )}
        {!settledNote && primaryAction && (
          <PrimaryActionSlot
            data-full-width={isCompact}
            data-min-height={minTouchTarget}
          >
            {primaryAction}
          </PrimaryActionSlot>
        )}
      </FooterRow>
    </InvoiceCardSection>
  );
};
